import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Screen } from '@/components/layout/Screen';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bird, Calendar, Hash, Loader2 } from 'lucide-react';
import { useCreateBatch } from '@/hooks/api/cbs';
import { getAuthenticatedClient, getCurrentUserId } from '@/services/client';
import { toast } from '@/hooks/use-toast';

export const NewBatchScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const createBatch = useCreateBatch();

  const [docCount, setDocCount] = useState('');
  const [placementDate, setPlacementDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [cycleDays, setCycleDays] = useState('42');
  const [selectedBreed, setSelectedBreed] = useState('');
  const [contractType, setContractType] = useState('integrator');

  // Fetch user's farm and profile (for country_id)
  const { data: farmProfile, isLoading: farmLoading } = useQuery({
    queryKey: ['user', 'farm-profile'],
    queryFn: async () => {
      const client = getAuthenticatedClient();
      const userId = getCurrentUserId();
      if (!userId) return null;
      const [farmRes, profileRes] = await Promise.all([
        client
          .from('farms')
          .select('id, name')
          .eq('user_id', userId)
          .eq('is_active', true)
          .limit(1)
          .maybeSingle(),
        client
          .from('profiles')
          .select('country_id')
          .eq('id', userId)
          .single(),
      ]);
      if (farmRes.error || !farmRes.data) return null;
      return {
        farm_id: farmRes.data.id,
        farm_name: farmRes.data.name,
        country_id: profileRes.data?.country_id ?? null,
      };
    },
  });

  // Fetch available breeds (independent of farm)
  const { data: breeds = [] } = useQuery({
    queryKey: ['breeds'],
    queryFn: async () => {
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from('breeds')
        .select('id, name, default_cycle_days, target_fcr, target_live_weight_kg')
        .eq('is_active', true)
        .order('name');
      if (error) return [];
      return data ?? [];
    },
    staleTime: 1000 * 60 * 30,
  });

  // Update cycle days when breed is selected
  const handleBreedChange = (breedId: string) => {
    setSelectedBreed(breedId);
    const breed = breeds.find((b: any) => b.id === breedId);
    if (breed) {
      setCycleDays(String(breed.default_cycle_days));
    }
  };

  const handleSubmit = async () => {
    const count = parseInt(docCount, 10);
    if (!count || count <= 0) {
      toast({ title: t("cbs.newBatch.invalidCount", { defaultValue: "Invalid count" }), description: t("cbs.newBatch.invalidCountDesc", { defaultValue: "Enter a valid number of birds." }), variant: 'destructive' });
      return;
    }
    if (!farmProfile) {
      toast({ title: t("cbs.newBatch.noFarmFound", { defaultValue: "No farm found" }), description: t("cbs.newBatch.noFarmFoundDesc", { defaultValue: "Please set up a farm first in your profile." }), variant: 'destructive' });
      return;
    }
    if (!farmProfile.country_id) {
      toast({ title: t("cbs.newBatch.noCountrySet", { defaultValue: "No country set" }), description: t("cbs.newBatch.noCountrySetDesc", { defaultValue: "Please set your country in your profile." }), variant: 'destructive' });
      return;
    }
    if (!selectedBreed) {
      toast({ title: t("cbs.newBatch.selectBreed", { defaultValue: "Select a breed" }), description: t("cbs.newBatch.selectBreedDesc", { defaultValue: "Choose the breed for this batch." }), variant: 'destructive' });
      return;
    }

    const userId = getCurrentUserId();
    if (!userId) return;

    const days = parseInt(cycleDays, 10) || 42;
    const expectedHarvest = new Date(placementDate);
    expectedHarvest.setDate(expectedHarvest.getDate() + days);

    try {
      await createBatch.mutateAsync({
        user_id: userId,
        farm_id: farmProfile.farm_id,
        breed_id: selectedBreed,
        country_id: farmProfile.country_id!,
        organization_id: null,
        batch_code: null,
        shed_number: null,
        doc_count: count,
        placement_date: placementDate,
        doc_source_id: null,
        planned_cycle_days: days,
        expected_harvest_date: expectedHarvest.toISOString().split('T')[0],
        actual_harvest_date: null,
        batch_status: 'active',
        status_changed_by: null,
        abandonment_reason: null,
        contract_type: contractType,
        current_bird_count: count,
        cumulative_mortality: 0,
        cumulative_feed_kg: 0,
        latest_avg_weight_kg: null,
        current_fcr: null,
        current_production_stage: 'brooding',
        notes: null,
      });
      navigate('/');
    } catch {
      // Error toast handled by the hook
    }
  };

  if (farmLoading) {
    return (
      <Screen showTopBar topBarProps={{ title: t("cbs.newBatch.title", { defaultValue: "New Batch" }), showBack: true }} showBottomNav={false}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Screen>
    );
  }

  return (
    <Screen showTopBar topBarProps={{ title: t('cbs.batch.new', { defaultValue: 'Start New Batch' }), showBack: true }} showBottomNav={false}>
      <div className="p-4 space-y-6 pb-20">
        {/* No Farm Warning */}
        {!farmProfile && (
          <Card className="p-6 text-center space-y-3">
            <div className="text-4xl">🏠</div>
            <h3 className="text-lg font-bold text-foreground">{t("cbs.newBatch.noFarmFound", { defaultValue: "No farm found" })}</h3>
            <p className="text-sm text-muted-foreground">
              {t("cbs.newBatch.noFarmFoundBody", { defaultValue: "Your account may not have a linked farm yet. Please contact your integrator or try logging in again." })}
            </p>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={() => navigate('/')}>{t("common.goHome")}</Button>
              <Button variant="outline" onClick={() => window.location.reload()}>{t("common.tryAgain")}</Button>
            </div>
          </Card>
        )}

        {/* Header */}
        {farmProfile && <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Bird className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-foreground">
              {t('cbs.batch.new', { defaultValue: 'Start New Batch' })}
            </h2>
            <p className="text-sm text-muted-foreground">
              {farmProfile.farm_name}
            </p>
          </div>
        </div>}

        <Card className="p-4 space-y-5">
          {/* Breed */}
          <div className="space-y-2">
            <Label htmlFor="breed">{t("cbs.newBatch.breed", { defaultValue: "Breed" })}</Label>
            <Select value={selectedBreed} onValueChange={handleBreedChange}>
              <SelectTrigger>
                <SelectValue placeholder={t("cbs.newBatch.selectBreedPlaceholder", { defaultValue: "Select breed" })} />
              </SelectTrigger>
              <SelectContent>
                {breeds.map((b: any) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name}
                    {b.target_live_weight_kg ? ` (${b.target_live_weight_kg}kg)` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Bird Count */}
          <div className="space-y-2">
            <Label htmlFor="docCount" className="flex items-center gap-2">
              <Hash className="w-4 h-4" />
              {t("cbs.newBatch.birdCount", { defaultValue: "Number of Birds (DOC)" })}
            </Label>
            <Input
              id="docCount"
              type="number"
              inputMode="numeric"
              placeholder={t("cbs.newBatch.birdCountPlaceholder", { defaultValue: "e.g. 2000" })}
              value={docCount}
              onChange={(e) => setDocCount(e.target.value)}
              min={1}
            />
          </div>

          {/* Placement Date */}
          <div className="space-y-2">
            <Label htmlFor="placementDate" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {t("cbs.newBatch.placementDate", { defaultValue: "Placement Date" })}
            </Label>
            <Input
              id="placementDate"
              type="date"
              value={placementDate}
              onChange={(e) => setPlacementDate(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">DD/MM/YYYY</p>
          </div>

          {/* Cycle Days */}
          <div className="space-y-2">
            <Label htmlFor="cycleDays">{t("cbs.newBatch.plannedCycleDays", { defaultValue: "Planned Cycle Days" })}</Label>
            <Input
              id="cycleDays"
              type="number"
              inputMode="numeric"
              placeholder="42"
              value={cycleDays}
              onChange={(e) => setCycleDays(e.target.value)}
              min={21}
              max={60}
            />
            <p className="text-xs text-muted-foreground">{t("cbs.newBatch.standardCycle", { defaultValue: "(standard broiler cycle)" })}</p>
          </div>

          {/* Contract Type */}
          <div className="space-y-2">
            <Label>{t("cbs.newBatch.contractType", { defaultValue: "Contract Type" })}</Label>
            <Select value={contractType} onValueChange={setContractType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="integrator">{t("cbs.newBatch.integrator", { defaultValue: "Integrator" })}</SelectItem>
                <SelectItem value="independent">{t("cbs.newBatch.independent", { defaultValue: "Independent" })}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Submit */}
        <Button
          className="w-full h-14 text-lg font-bold gap-2"
          onClick={handleSubmit}
          disabled={createBatch.isPending || !selectedBreed || !docCount}
        >
          {createBatch.isPending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Bird className="w-5 h-5" />
          )}
          {createBatch.isPending ? t("cbs.newBatch.creating", { defaultValue: "Creating..." }) : t("cbs.newBatch.startBatch", { defaultValue: "Start Batch" })}
        </Button>
      </div>
    </Screen>
  );
};
